import { EMAIL_TEMPLATE_CATEGORIES, EMAIL_TEMPLATE_CATEGORY_LABELS, type EmailTemplateCategory, type EmailTemplateDTO } from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Dialog, Field, Input, Select, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';

export type SaveAsTemplateDialogProps = {
  subject: string;
  body: string;
  defaultName: string;
  defaultCategory: EmailTemplateCategory;
  onClose: () => void;
  /** Called once the template exists and the template lists were refreshed, so a picker can select it. */
  onSaved: (template: EmailTemplateDTO) => void;
};

/**
 * Keeps an email written in a composer as a reusable template, {{variables}} included. Needs templates:manage —
 * callers hide the action for everyone else. The API refuses unknown variables; both composers already list them
 * in their review, so a refusal only needs the server's message.
 */
export function SaveAsTemplateDialog({ subject, body, defaultName, defaultCategory, onClose, onSaved }: SaveAsTemplateDialogProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(defaultName);
  const [category, setCategory] = useState<EmailTemplateCategory>(defaultCategory);

  const save = useMutation({
    mutationFn: () => api.post<EmailTemplateDTO>('/api/templates', { name: name.trim(), category, subject, body }),
    onSuccess: async (template) => {
      toast.success(`Saved “${template.name}” as a template`);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      onSaved(template);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!save.isPending}
      title="Save as template"
      description="Saves the subject and message as they are now. Variables stay variables, so the template fits any lead."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={name.trim() === ''} onClick={() => save.mutate()}>
            Save template
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label="Template name">
          <Input value={name} maxLength={200} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Category">
          <Select
            value={category}
            onChange={(event) => setCategory(event.target.value as EmailTemplateCategory)}
            options={EMAIL_TEMPLATE_CATEGORIES.map((value) => ({ value, label: EMAIL_TEMPLATE_CATEGORY_LABELS[value] }))}
          />
        </Field>
      </div>
    </Dialog>
  );
}
